// The page of the interface as data: every control component, and the regions (containers and fixtures) they are
// drawn in, in document order. plan() says what each region holds for what the preset shows now; ui.js puts it on
// the page. No page access here.
import { html } from '../dom.js';
import { plan } from './layout.js';
import { topParts } from './top.js';
import { timeParts } from './time.js';
import { exploreParts } from './explore.js';
import { columnParts } from './column.js';
import { cardParts } from './card.js';
import { customParts } from './custom.js';
import { lobbyPanel } from './lobby.js';
import { phoneParts, phoneOn } from './phone.js';

/** @param c the context (context.js)  @param hooks { renderGallery, bookingButton, debug } for the place card */
export function createPage(c, hooks) {
  const T = c.T;
  const top = topParts(c), time = timeParts(c), explore = exploreParts(c), column = columnParts(c), lobby = lobbyPanel(c), card = cardParts(c, hooks);
  const custom = customParts(c); // the package's own controls (`x.`), drawn from the settings list
  const phone = (c.ph = phoneParts(c)); // VIS-230: a phone's rail, sheets and docks (phone.js)
  // control id -> { can?(): its data is there, html?(): its markup, wire?(root): its behaviour }
  const PARTS = { ...top.parts, ...time.parts, ...explore.parts, ...column.parts, ...card.parts, 'map.pick': {}, ...custom.parts };
  // a plain container: the slot's controls in the layout's order
  const row = (slot) => ({ slot, html: (kids) => (kids.some((k) => k.html) ? html`<div class="${slot}">${kids.map((k) => k.html)}</div>` : '') });
  // A region with a `slot` is the container of that slot's controls (`only`: the ones its frame knows how to place);
  // the others are fixtures. `key` is what else a region's markup depends on. (WS-91 part 2) `skip`: the slot's
  // controls another region draws.
  const REGIONS = Object.entries({
    'top-left': row('top-left'),
    'top-actions': row('top-actions'),
    left: { ...explore.panel, only: ['explore.panel', 'explore.search', 'explore.categories', 'explore.legend'] },
    card: { key: () => c.layout.panel, html: () => (c.layout.panel ? lobby.html() : html`<article id="card" class="shell" aria-live="polite"></article>`) },
    dock: { ...time.dock, only: ['time.dates', 'time.pickDate', 'time.hour', 'time.play'] },
    controls: column.column,
    wayfind: column.wayfind, // WS-91 part 2: "Where to?" on its own at the top middle of the map, not in the column
    toast: { html: () => html`<div id="toast" role="status" aria-live="polite"></div>` },
    'm-explore': explore.mobile,
    ph: phone.region, // VIS-230: the phone's rail, its sheet and the tour's caption (empty off phones)
    tooltip: { html: () => html`<div id="tooltip" hidden></div>` },
    attract: { html: () => html`<div class="attract-cta">${T('attract')}</div>` },
  });

  /**
   * Reads what is shown, the layout and the language, and returns the regions in document order:
   * `{ name, region, ids, key, html() }` (`ids`: the controls drawn in it; `key`: changes when its markup would).
   */
  function layout() {
    c.read();
    c.phone = phoneOn(c.narrow, c.layout, c.kiosk); // VIS-230: layout B (ui.js sets c.narrow and c.kiosk)
    const shown = c.shown(), can = (id) => !!PARTS[id] && (PARTS[id].can?.() ?? true);
    c.slots = custom.place(plan(c.layout, shown, can), shown);
    // WS-51 ghosts (editor mode only, `c.ghostsOn`): the controls the preset hides are drawn too, in the place they
    // would have, and marked by ui.js (dimmed; VIS-73: still pressable). They are in no slot of `c.slots` and not in `c.drawn`, so
    // nothing behaves as if they were shown; a visitor's page never takes this path.
    const every = c.ghostsOn ? c.rt.controls().map((x) => x.id) : null, all = every ? custom.place(plan(c.layout, every, can), every) : c.slots;
    const ids = Object.fromEntries(REGIONS.map(([name, r]) => [name, (all[r.slot] || []).filter((id) => (!r.only || r.only.includes(id)) && !r.skip?.includes(id))]));
    c.ghosts = new Set(every ? Object.values(ids).flat().filter((id) => !shown.includes(id)) : []); // only what a region draws: a card part or picking has no place of its own
    c.drawn = new Set([...Object.values(ids).flat().filter((id) => !c.ghosts.has(id)), ...(c.slots.card || []), ...(c.slots.map || [])]);
    return REGIONS.map(([name, region]) => ({ name, region, ids: ids[name], key: `${ids[name]}|${region.key?.() ?? ''}`,
      html: () => region.html(ids[name].map((id) => ({ id, html: PARTS[id].html?.() ?? '' }))) }));
  }

  return { top, time, explore, column, lobby, card, phone, PARTS, REGIONS, order: REGIONS.map(([name]) => name), layout }; // WS-60: a map style replaces REGIONS entries; VIS-230: and may reorder phone.rows / phone.tools
}
